"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="28277" className="flex gap-8 animate-pulse">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-4 w-20 bg-muted/20 rounded-sm" />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.slice(5).map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="font-sans text-sm font-black uppercase tracking-tighter text-foreground hover:text-primary transition-all duration-300 relative group">
                {item.label}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 p-8 animate-pulse">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-6 w-32 bg-muted/20 rounded-sm" />)}
      </div>;
  }
  return <nav className="flex flex-col items-center gap-8 p-8">
      {menuItems.slice(5).map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="font-sans text-2xl font-black uppercase tracking-tighter text-foreground hover:text-primary transition-colors" onClick={onClose}>
          {item.label}
        </Link>)}
      <Button className="mt-4 w-full max-w-xs rounded-full bg-gradient-to-r from-secondary to-orange-600 text-secondary-foreground hover:opacity-90 transition-all font-black uppercase tracking-tighter shadow-lg shadow-secondary/20" onClick={onClose} asChild>
        <Link to="/#zarezerwuj-swoje-miejsce">Zapisz się</Link>
      </Button>
    </nav>;
}
export default function Naglowek() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className={`sticky top-0 z-50 w-full transition-all duration-500 dark bg-background text-foreground ${isScrolled ? "bg-background/80 backdrop-blur-md border-b border-primary/20 shadow-xl" : "bg-background border-b border-transparent"}`}>
      <div className="container mx-auto px-4 md:px-6 h-20 flex items-center justify-between">
        {}
        <div className="flex-shrink-0 w-[220px]">
          <Link to="/StartPage" className="block">
            <img src="https://newwavecamp.com/wp-content/uploads/2026/03/popraw-te-niedoskonalosci-i-przytnij-zdjecie-do-wymiarow-logo-pod-strone-interne-scaled.jpg" alt="NowaFala Logo" className="object-contain !rounded-none !opacity-[1] !w-[176.1px] !h-[80px] transition-all duration-300" />
          </Link>
        </div>

        {}
        <div className="hidden md:flex flex-1 justify-center">
          <MenuProvider menu_id="16" className="contents">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="flex items-center justify-end gap-4 w-[220px]">
          <Button asChild className="hidden md:inline-flex rounded-full bg-gradient-to-r from-secondary to-orange-600 text-secondary-foreground hover:opacity-90 hover:scale-105 transition-all font-black uppercase tracking-tighter px-10 h-12 text-base shadow-lg shadow-secondary/20 animate-pulse-subtle">
            <Link to="/#zarezerwuj-swoje-miejsce">Zapisz się</Link>
          </Button>

          {}
          <Button variant="ghost" size="icon" className="md:hidden text-foreground hover:text-primary hover:bg-transparent" onClick={() => setIsMobileMenuOpen(true)} aria-label="Open menu">
            <Menu className="h-7 w-7" />
          </Button>
        </div>
      </div>

      {}
      {isMobileMenuOpen && <div className="fixed inset-0 z-50 bg-background md:hidden flex flex-col dark">
          <div className="container mx-auto px-4 h-20 flex items-center justify-between border-b border-primary/20">
            <Link to="/StartPage" onClick={() => setIsMobileMenuOpen(false)}>
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1583198/img/new_wave_camp_logo.png" alt="NowaFala Logo" className="h-9 w-auto object-contain transition-all duration-300" />
            </Link>
            <Button variant="ghost" size="icon" className="text-foreground hover:text-primary hover:bg-transparent" onClick={() => setIsMobileMenuOpen(false)} aria-label="Close menu">
              <X className="h-7 w-7" />
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto py-12">
            <MenuProvider menu_id="16" className="contents">
              <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
            </MenuProvider>
          </div>
        </div>}
    </header>;
}